import { PayloadAction } from "@reduxjs/toolkit";
import { SagaIterator } from "redux-saga";
import { call, put, select, takeLatest } from "redux-saga/effects";
import {
  selectActivationCode,
  selectSessionToken,
  selectUserCidpId,
} from "scenes/selectors";
import { mfaFlow } from "utils/enum";
import factory from "utils/getApiHandlerEffect";
import {
  EnvAuthenticatorStatusEnum,
  PassCodeSubmissionStatusEnum,
} from "./enums";
import {
  afterEnvAuthenticatorFetch,
  afterPassCodeSubmission,
  afterStatusFetch,
  loadQrCode,
  loadStatus,
  setError,
  setStatus,
  submitPasscode,
} from "./redux";
import { currentFlow } from "./selectors";
import {
  PassCodeRequestPayload,
  QRCodeRequestPayload,
  QRCodeResponse,
} from "./types";

export function* onLoad(
  action: PayloadAction<QRCodeRequestPayload>
): SagaIterator<void> {
  const { authenticatorType, currentLayout } = action.payload;
  const activationCode = yield select(selectActivationCode);
  const cidpId = yield select(selectUserCidpId);
  const apiHandler: APIHandler = yield call(() => factory());
  const authToken = yield select(selectSessionToken);

  try {
    const envQrCodeDetails: QRCodeResponse = yield call(() =>
      currentLayout === mfaFlow.MFA
        ? apiHandler.putUpdateMFA("app", cidpId, { factorType: "TOTP" })
        : apiHandler.putMFAEnroll(authenticatorType, activationCode)
    );

    const response = yield call(() =>
      fetch(envQrCodeDetails.qrCodeLink, {
        method: "GET",
        headers: new Headers({
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "image/png",
        }),
      })
    );

    const envAuthQrCodeImg = yield response.blob();
    const envAuthQrCodeImgUrl = URL.createObjectURL(envAuthQrCodeImg);

    yield put(
      afterEnvAuthenticatorFetch({
        qrCodeImg: envAuthQrCodeImgUrl,
        secretCode: envQrCodeDetails.secretCode,
      })
    );
  } catch (e: any) {
    if (e.statusCode === 400) {
      yield put(
        afterEnvAuthenticatorFetch({ qrCodeImg: undefined, secretCode: "" })
      );
    } else {
      yield put(setError(true));
    }
  }
}

export function* onPassCodeSubmission(
  action: PayloadAction<PassCodeRequestPayload>
): SagaIterator<void> {
  yield put(afterPassCodeSubmission(PassCodeSubmissionStatusEnum.PROCESSING));
  const envActivationCode = yield select(selectActivationCode);
  const cidpId = yield select(selectUserCidpId);
  const currentEnvFlowSetup = yield select(currentFlow);
  const { authenticatorType, passcode } = action.payload;
  const apiHandler: APIHandler = yield call(() => factory());
  try {
    yield call(() =>
      currentEnvFlowSetup === mfaFlow.MFA
        ? apiHandler.updatePassCode("app", cidpId, passcode)
        : apiHandler.submitAuthenticatorPassCode(
            authenticatorType,
            envActivationCode,
            passcode
          )
    );
    yield put(setError(true));
    yield put(afterPassCodeSubmission(PassCodeSubmissionStatusEnum.DONE));
  } catch (e: any) {
    yield put(setError(true));
    yield put(afterPassCodeSubmission(PassCodeSubmissionStatusEnum.ERROR));
  }
}

export function* onLoadStatus(
  action: PayloadAction<QRCodeRequestPayload>
): SagaIterator<void> {
  const { authenticatorType } = action.payload;
  const cidpId = yield select(selectUserCidpId);
  const currentEnvFlowSetup = yield select(currentFlow);
  const envActivationCode = yield select(selectActivationCode);

  yield put(setStatus(EnvAuthenticatorStatusEnum.PROCESSING));
  const apiHandler = yield call(() => factory());
  try {
    const response: { status: EnvAuthenticatorStatusEnum } = yield call(() =>
      currentEnvFlowSetup === mfaFlow.MFA
        ? apiHandler.getUpdateMfaStatus("app", cidpId)
        : apiHandler.getAuthenticatorStatus(
            authenticatorType,
            envActivationCode
          )
    );
    yield put(afterStatusFetch(response.status));
  } catch (e: any) {
    if (e.statusCode === 400) {
      yield put(afterStatusFetch(EnvAuthenticatorStatusEnum.ERROR));
    } else {
      yield put(setError(true));
    }
  }
}

// watcher function
export default function* envAuthenticatorSaga() {
  yield takeLatest(loadQrCode.type, onLoad);
  yield takeLatest(submitPasscode.type, onPassCodeSubmission);
  yield takeLatest(loadStatus.type, onLoadStatus);
}
